/**
 * Altar del Perfume — ampliar las fotos de la ficha de producto.
 *
 * Cada foto de la galería es un botón que abre un <dialog> a pantalla completa con la misma
 * foto en 2400 px (se carga sólo al abrir). Dentro: flechas, teclado (← → Esc), deslizar en el
 * celular, y un toque o clic sobre la foto para acercarla al doble; con el acercamiento puesto,
 * mover el puntero o el dedo recorre la foto. El <dialog> nativo da el foco atrapado y Esc.
 */
class AltarZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.slides = [...this.querySelectorAll('[data-altar-zoom-slide]')];
    this.count = this.querySelector('[data-altar-zoom-count]');
    if (!this.dialog || !this.slides.length) return;

    const root = this.closest('.altar-pdp') || document;
    root.querySelectorAll('[data-altar-zoom-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.open(Number(btn.dataset.altarZoomOpen), btn));
    });

    this.querySelector('[data-altar-zoom-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-altar-zoom-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-altar-zoom-next]')?.addEventListener('click', () => this.go(1));

    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowRight') this.go(1);
    });
    this.dialog.addEventListener('close', () => {
      this.unzoom();
      document.documentElement.style.overflow = '';
      this.opener?.focus();
    });
    // Clic en el fondo (fuera de la foto y de los botones) cierra.
    this.dialog.addEventListener('click', (e) => {
      if (e.target.classList.contains('altar-zoom__slide') || e.target.classList.contains('altar-zoom__stage')) {
        this.dialog.close();
      }
    });

    this.slides.forEach((slide) => {
      const img = slide.querySelector('img');
      img.addEventListener('click', (e) => {
        if (this.moved) return;
        slide.classList.toggle('is-zoomed');
        if (slide.classList.contains('is-zoomed')) this.pan(slide, e);
        else img.style.transformOrigin = '';
      });
      slide.addEventListener('pointermove', (e) => {
        if (slide.classList.contains('is-zoomed')) this.pan(slide, e);
      });
    });

    // Deslizar para cambiar de foto (sólo sin acercamiento).
    let x0 = null;
    this.dialog.addEventListener('pointerdown', (e) => {
      x0 = e.clientX;
      this.moved = false;
    });
    this.dialog.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      const zoomed = this.slides[this.index]?.classList.contains('is-zoomed');
      if (!zoomed && Math.abs(dx) > 50 && e.pointerType !== 'mouse') {
        this.moved = true;
        this.go(dx < 0 ? 1 : -1);
        setTimeout(() => (this.moved = false), 50);
      }
    });
  }

  pan(slide, e) {
    const img = slide.querySelector('img');
    // Se mide contra el marco (que no se escala), no contra la foto ya ampliada.
    const r = slide.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
    img.style.transformOrigin = `${x}% ${y}%`;
  }

  unzoom() {
    this.slides.forEach((s) => {
      s.classList.remove('is-zoomed');
      s.querySelector('img').style.transformOrigin = '';
    });
  }

  show(i) {
    const n = this.slides.length;
    this.index = (i + n) % n;
    this.unzoom();
    this.slides.forEach((s, k) => {
      s.hidden = k !== this.index;
    });
    const img = this.slides[this.index].querySelector('img');
    if (!img.src && img.dataset.src) img.src = img.dataset.src;
    // Precargar la siguiente para que la flecha responda al instante.
    const next = this.slides[(this.index + 1) % n].querySelector('img');
    if (!next.src && next.dataset.src) next.src = next.dataset.src;
    if (this.count) this.count.textContent = n > 1 ? `${this.index + 1} / ${n}` : '';
  }

  go(step) {
    if (this.slides.length > 1) this.show(this.index + step);
  }

  open(i, opener) {
    this.opener = opener;
    this.show(i);
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
  }
}

if (!customElements.get('altar-zoom')) customElements.define('altar-zoom', AltarZoom);
